// src/pages/Auth/VerifyEmail.js - UPDATED WITH FULL FUNCTIONALITY
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { COLORS } from '../../utils/constants';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired, invalid
  const [message, setMessage] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmailToken();
    }
  }, [token]);

  const verifyEmailToken = async () => {
    try {
      const result = await authService.verifyEmail(token);
      
      if (result.verified) {
        setStatus('success');
        setMessage(result.message || 'Email verified successfully!');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/', { 
            state: { 
              message: 'Email verified! Please login to continue.',
              verified: true 
            }
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      // Handle different error types
      if (error.message.includes('expired') || error.message.includes('Expired')) {
        setStatus('expired');
        setMessage('Your verification link has expired.');
        setCanResend(true);
      } else if (error.message.includes('Invalid') || error.message.includes('invalid')) {
        setStatus('invalid');
        setMessage('This verification link is invalid.');
      } else if (error.message.includes('already verified')) {
        setStatus('success');
        setMessage('Email is already verified. You can login to your account.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(error.message || 'Verification failed. Please try again.');
        setCanResend(true);
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email) {
      alert('Please enter your email address');
      return;
    }

    setResending(true);
    setResendSuccess(false);

    try {
      await authService.resendVerificationEmail(email);
      setResendSuccess(true);
      setMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      alert(error.message || 'Failed to resend email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  const handleGoToSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-content">
        <div className="verify-email-card">
          {/* Logo/Icon */}
          <div className="verify-email-logo">
            {status === 'verifying' && '📧'}
            {status === 'success' && '✓'}
            {(status === 'error' || status === 'invalid') && '✗'}
            {status === 'expired' && '⏱️'}
          </div>

          <h2>Email Verification</h2>
          
          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="verification-status">
              <div className="loading-spinner"></div>
              <p>Verifying your email address...</p>
              <p className="sub-text">Please wait a moment</p>
            </div>
          )}
          
          {/* Success State */}
          {status === 'success' && (
            <div className="verification-status success">
              <div className="status-icon success-icon">✓</div>
              <p className="status-message">{message}</p>
              <p className="redirect-message">Redirecting to login page...</p>
              <button 
                onClick={handleGoToLogin}
                style={{ backgroundColor: COLORS.PRIMARY }}
                className="verify-btn"
              >
                Go to Login Now
              </button>
            </div>
          )}
          
          {/* Expired State */}
          {status === 'expired' && (
            <div className="verification-status error">
              <div className="status-icon error-icon">⏱️</div>
              <p className="status-message">{message}</p>
              
              {canResend && (
                <div className="resend-section">
                  <p className="resend-label">Enter your email to receive a new verification link:</p>
                  <input
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="email-input"
                  />
                  <button
                    onClick={handleResendEmail}
                    disabled={resending}
                    style={{ backgroundColor: COLORS.PRIMARY }}
                    className="verify-btn"
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                  {resendSuccess && (
                    <p className="success-message">✓ Email sent successfully! Check your inbox.</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Invalid State */}
          {status === 'invalid' && (
            <div className="verification-status error">
              <div className="status-icon error-icon">✗</div>
              <p className="status-message">{message}</p>
              <p className="sub-text">This link may have already been used or is invalid.</p>
              <button 
                onClick={handleGoToLogin}
                style={{ backgroundColor: COLORS.PRIMARY }}
                className="verify-btn"
              >
                Go to Login
              </button>
            </div>
          )}
          
          {/* Generic Error State */}
          {status === 'error' && (
            <div className="verification-status error">
              <div className="status-icon error-icon">⚠️</div>
              <p className="status-message">{message}</p>
              
              {canResend ? (
                <div className="resend-section">
                  <p className="resend-label">Enter your email to resend verification:</p>
                  <input
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="email-input"
                  />
                  <button
                    onClick={handleResendEmail}
                    disabled={resending}
                    style={{ backgroundColor: COLORS.PRIMARY }}
                    className="verify-btn"
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                  {resendSuccess && (
                    <p className="success-message">✓ Email sent successfully!</p>
                  )}
                </div>
              ) : (
                <div className="error-actions">
                  <button 
                    onClick={handleGoToSignup}
                    className="verify-btn secondary"
                  >
                    Back to Signup
                  </button>
                  <button 
                    onClick={handleGoToLogin}
                    style={{ backgroundColor: COLORS.PRIMARY }}
                    className="verify-btn"
                  >
                    Try Login
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="verify-email-footer">
            <p>Need help? <a href="mailto:support@yourdomain.com">Contact Support</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;